import type {
  BrowserBrand,
  HighEntropyBrowserHints,
  LowEntropyBrowserHints,
} from '@snapchat/client-hints';
import {
  AbstractBrowserFeature,
  parseBrowserBrand,
  parseMajorVersion,
  parsePlatform,
  parseUserAgent,
} from '@snapchat/client-hints';

import type { BrowserFeaturesWindow, HighEntropyUserAgentValues } from './navigatorData';

/**
 * Keys to the getHighEntropyValues function based on the client hints requested.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/API/NavigatorUAData/getHighEntropyValues
 */
const highEntropyUserAgentHints: Partial<
  Record<keyof HighEntropyBrowserHints, keyof HighEntropyUserAgentValues>
> = {
  platformVersion: 'platformVersion',
  architecture: 'architecture',
  bitness: 'bitness',
  model: 'model',
  fullBrowsers: 'fullVersionList',
};

/** Browser feature looking for the client. */
export class ClientBrowserFeature extends AbstractBrowserFeature {
  private readonly featuresWindow: BrowserFeaturesWindow;

  constructor(featuresWindow: BrowserFeaturesWindow = window as BrowserFeaturesWindow) {
    super({
      /* Nothing here. Can use window object. */
    });
    this.featuresWindow = featuresWindow;
  }

  /** @override */
  protected computeLowEntropyHints(): LowEntropyBrowserHints {
    const legacy = parseUserAgent(this.featuresWindow.navigator.userAgent);
    const browsers = this.featuresWindow.navigator.userAgentData?.brands?.map(brand => ({
      brand: parseBrowserBrand(brand.brand),
      majorVersion: Number(parseMajorVersion(brand.version)),
    }));

    return {
      browsers: browsers ?? legacy.browsers,
      platform:
        parsePlatform(this.featuresWindow.navigator.userAgentData?.platform) ?? legacy.platform,
      isMobile: this.featuresWindow.navigator.userAgentData?.mobile ?? legacy.isMobile,
      saveData: this.featuresWindow.navigator.connection?.saveData ?? legacy.isMobile,
    };
  }

  /** @override */
  protected async computeHighEntropyHintsAsync(
    ...hints: Array<keyof HighEntropyBrowserHints>
  ): Promise<HighEntropyBrowserHints> {
    const highEntropyValues =
      await this.featuresWindow.navigator.userAgentData?.getHighEntropyValues(
        hints.map(hint => highEntropyUserAgentHints[hint]!).filter(Boolean)
      );
    const reduceMotion = this.featuresWindow.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const preferDark = this.featuresWindow.matchMedia('(prefers-color-scheme: dark)').matches;
    const preferLight = this.featuresWindow.matchMedia('(prefers-color-scheme: light)').matches;
    return {
      viewportWidth: this.featuresWindow.innerWidth,
      viewportHeight: this.featuresWindow.innerHeight,
      fullBrowsers: highEntropyValues?.fullVersionList?.map(brand => ({
        brand: brand.brand as BrowserBrand,
        fullVersion: brand.version,
      })),
      model: highEntropyValues?.model,
      downlinkMbps: this.featuresWindow.navigator.connection?.downlink,
      devicePixelRatio: this.featuresWindow.devicePixelRatio,
      connection: this.featuresWindow.navigator.connection?.effectiveType,
      architecture: highEntropyValues?.architecture,
      bitness: highEntropyValues?.bitness ? Number(highEntropyValues?.bitness) : undefined,
      reduceMotion,
      colorScheme: preferDark ? 'dark' : preferLight ? 'light' : undefined,
      platformVersion: highEntropyValues?.platformVersion,
    };
  }
}
